import { useObserver } from 'mobx-react-lite';

import useUser from 'src/hooks/use-user';
import { useModals, ModalNames } from 'src/components/modals';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { useLoginNudgeContext } from './login-nudge-context';

type UseLoginNudgeReturn = {
  shouldShowNudge: boolean;
  dismissNudge: () => void;
  handleNudgeClick: () => void;
};

export const useLoginNudge = (): UseLoginNudgeReturn => {
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const isLoading = useObserver(() => User.loading);

  const { openModal } = useModals();
  const { isDismissed, dismissNudge } = useLoginNudgeContext();
  // The nudge lives in the logged-out menu header (a non-Pay surface), so don't mint/persist a session token
  // just to emit this event — only stamp one if the guest already has it.
  const { trackLoginNudge } = useDutchiePayAnalytics({ mintSessionToken: false });

  const shouldShowNudge = !isLoading && !isLoggedIn && !isDismissed;

  const handleNudgeClick = (): void => {
    trackLoginNudge({ action: 'clicked', variant: 'header_social_login' });
    dismissNudge();
    void openModal(ModalNames.login);
  };

  const conditionallyDismissNudge = (): void => {
    if (shouldShowNudge) {
      dismissNudge();
    }
  };

  return {
    shouldShowNudge,
    dismissNudge: conditionallyDismissNudge,
    handleNudgeClick,
  };
};
